import { Component, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, ValidationErrors, AbstractControl } from '@angular/forms';
import { TextHelper } from '../../helpers/text.helper';
import { NumberHelper } from '../../helpers/number.helper';
import { CurrencyHelper } from '../../helpers/currency.helper';

@Component({
  selector: 'shared-input-number-simple',
  templateUrl: './input-number-simple.component.html',
  styleUrls: ['./input-number-simple.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputNumberSimpleComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class InputNumberSimpleComponent implements ControlValueAccessor {
  // Template references
  @ViewChild('valueInput', { static: true }) inputElm: ElementRef<HTMLInputElement>;
  @ViewChild('suffix', { static: true }) suffixElm: ElementRef<HTMLElement>;

  // Input properties
  @Input() formControlName = '';
  @Input() unit: string;
  @Input() decimals = 3;
  @Input() step = 1;
  @Input() max = 999999999999999;
  @Input() min: number | null = 0;
  @Input() maxLength?: number; // Allow custom maxLength, calculated automatically if not provided
  @Input() disabled: boolean;
  @Input() currency = false;
  @Input() width: number | 'auto' = 'auto';
  @Input() fixValueOnBlur = false;

  // Output events
  @Output() valueChange = new EventEmitter<number>();

  // Private properties
  private _value: number | null = null;
  private previousValidValue?: number = null;
  private touched = false;

  // ControlValueAccessor callbacks
  private onChange = (value: number | null): void => {
    this.valueChange.emit(value);
    this.updateSuffix(value);
  };

  private onTouched = (): void => {};
  private onValidatorChange = (): void => {};

  // Value getter and setter
  @Input() get value(): number | null {
    return this._value;
  }

  set value(val: number | null) {
    this._value = val;
    this.updateSuffix(val);
  }

  // String value getter and setter for template binding
  get stringValue(): string {
    return NumberHelper.stringify(this.value);
  }

  set stringValue(stringValue: string) {
    this.markAsTouched();
    this.handleValueChange(stringValue);
  }

  // Unit value getter for template
  get unitValue(): string {
    if (this.unit) {
      return this.unit;
    }
    return this.currency ? CurrencyHelper.currencySymbol : '';
  }

  // Calculated max length getter
  get calculatedMaxLength(): number {
    if (this.maxLength != null) {
      return this.maxLength;
    }
    return this.calculateMaxLength();
  }

  // Public methods
  focusInput(): void {
    if (this.inputElm?.nativeElement) {
      this.inputElm.nativeElement.focus();
    }
  }

  onBlurEvent($event: FocusEvent): void {
    if (this.fixValueOnBlur && !NumberHelper.isValidNumber(this.value, this.min, this.max)) {
      this.writeValue(this.previousValidValue);
      this.onChange(this.value);
    }
  }

  // Private methods
  private handleValueChange(stringValue: string): void {
    let numericValue = NumberHelper.safeValue(stringValue.trim());

    if (numericValue != null) {
      if (this.isValueOutOfRange(numericValue)) {
        // Revert to previous valid value
        numericValue = this.previousValidValue;
        // Force update the input field display
        this.forceInputUpdate();
      } else {
        // Store current value as the new previous valid value
        this.previousValidValue = numericValue;
      }
    }

    this.writeValue(numericValue);
    this.onChange(this.value);
    this.updateSuffix(this.value);
  }

  private forceInputUpdate(): void {
    // Use setTimeout to ensure the value is updated after the current change detection cycle
    setTimeout(() => {
      if (this.inputElm?.nativeElement) {
        this.inputElm.nativeElement.value = NumberHelper.stringify(this.previousValidValue) || '';
      }
    }, 0);
  }

  private isValueOutOfRange(value: number): boolean {
    return value > this.max || (this.min != null && value < this.min);
  }

  private calculateMaxLength(): number {
    const maxString = this.max.toString();
    const decimalPart = this.decimals > 0 ? this.decimals + 1 : 0; // +1 for decimal point
    const signPart = this.min != null && this.min < 0 ? 1 : 0; // +1 for negative sign
    return maxString.length + decimalPart + signPart;
  }

  private markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  private updateSuffix(value?: number | string): void {
    const elm = this.inputElm.nativeElement;

    if (!value || value === '') {
      this.suffixElm.nativeElement.style.visibility = 'hidden';
      return;
    }

    const width = TextHelper.getTextWidth(
      NumberHelper.stringify(value),
      TextHelper.getCanvasFont(elm),
      6,
    );

    this.suffixElm.nativeElement.style.visibility = 'visible';
    this.suffixElm.nativeElement.style.left = width + 'px';
  }

  // ControlValueAccessor implementation
  writeValue(value: number): void {
    if (NumberHelper.isValidNumber(value, this.min, this.max)) {
      // Initialize or update previousValidValue with valid values
      if (value != null) {
        this.previousValidValue = value;
      }
    }
    this.value = value;
    this.updateSuffix(value);
  }

  registerOnChange(onChange: (value: number) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  // Validator implementation
  registerOnValidatorChange?(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value: number = control.value as number | null;
    if (value == null || this.min == null || (value <= this.max && value >= this.min)) {
      return null;
    }
    return { invalidValue: { value } };
  }
}
